/**
 * YD Product Sequence
 * 产品序列帧动画模块 (275帧版本)
 *
 * 状态流程 (桌面端 - Scrub 模式):
 * LOADING → INTRO (0-50帧自动播放)
 *   → INTRO_LOOP (50-75帧循环,等待用户滚动)
 *   → SCRUB (75-125帧 ScrollTrigger scrub)
 *   → FINAL_LOOP (125-174帧循环播放，顶部第二个标题保持)
 *
 * 状态流程 (移动端 - 与桌面一致):
 * LOADING → INTRO (0-50帧自动播放)
 *   → INTRO_LOOP (50-75帧循环,等待用户滑动)
 *   → SCRUB (75-125帧 ScrollTrigger scrub)
 *   → FINAL_LOOP (125-174帧循环播放，顶部第二个标题保持)
 */
(function() {
  'use strict';

  /**
   * 序列帧加载器：实现见 assets/yd-sequence-loader.js（全站共用）。
   * 优先帧阶段进度按优先帧数计算；单帧失败 reject。
   */
  class LocalSequenceLoader extends window.YDSequenceLoader {
    constructor(config) {
      super({
        totalFrames: config.totalFrames,
        basePath: config.basePath,
        avifPath: config.avifPath,
        fallbackPath: config.fallbackPath,
        framePad: config.framePad,
        frameStart: config.frameStart,
        priorityBase: config.priorityBase,
        progressMode: 'priority'
      });
    }
  }

  /**
   * Canvas 渲染器
   */
  class CanvasRenderer {
    constructor(canvasId) {
      this.canvas = document.querySelector(canvasId);
      if (!this.canvas) {
        throw new Error('Canvas not found: ' + canvasId);
      }

      this.ctx = this.canvas.getContext('2d');
      this.resizeCanvas();

      // 200ms debounce 避免移动端地址栏滚动狂触发 resize
      let resizeTimer = null;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => this.resizeCanvas(), 200);
      });
    }

    resizeCanvas() {
      // dpr 上限：移动端 1，桌面 ≤2，且不超过「源帧宽 / CSS 宽」。
      // 位图比源帧更大只会把每帧 clearRect + drawImage 的填充面积放大（1920 源帧画到 3840 位图是 4 倍），
      // 清晰度受源帧限制并不会提升；源帧宽在首次 renderFrame 时得知并触发一次重算。
      const isMobile = window.innerWidth < 768;
      const rect = this.canvas.getBoundingClientRect();
      let dpr = isMobile ? 1 : Math.min(window.devicePixelRatio || 1, 2);
      if (this.sourceWidth && rect.width > 0) {
        dpr = Math.min(dpr, Math.max(1, this.sourceWidth / rect.width));
      }

      this.canvas.width = Math.round(rect.width * dpr);
      this.canvas.height = Math.round(rect.height * dpr);

      // 赋值 width/height 已重置变换矩阵，这里直接设定而非累积 scale
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.canvasWidth = rect.width;
      this.canvasHeight = rect.height;
    }

    /**
     * 渲染帧到 Canvas (cover 模式)
     */
    renderFrame(img) {
      if (!img) return;

      // 记录最大源帧宽（主素材与 FINAL_LOOP 素材尺寸可能不同），变大时按新上限重算位图
      const srcW = img.naturalWidth || img.width;
      if (srcW && srcW > (this.sourceWidth || 0)) {
        this.sourceWidth = srcW;
        this.resizeCanvas();
      }

      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      const imgAspect = img.width / img.height;
      const canvasAspect = this.canvasWidth / this.canvasHeight;

      let drawWidth, drawHeight, drawX, drawY;

      if (imgAspect > canvasAspect) {
        drawHeight = this.canvasHeight;
        drawWidth = this.canvasHeight * imgAspect;
        drawX = (this.canvasWidth - drawWidth) / 2;
        drawY = 0;
      } else {
        drawWidth = this.canvasWidth;
        drawHeight = this.canvasWidth / imgAspect;
        drawX = 0;
        drawY = (this.canvasHeight - drawHeight) / 2;
      }

      this.ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    }

    clear() {
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    }
  }

  /**
   * 产品序列帧状态机 - 基类
   *
   * 共享桌面端和移动端的公共逻辑
   */
  class ProductSequenceStateMachineBase {
    constructor(config, loader, renderer, deviceType) {
      this.config = config;
      this.loader = loader;
      this.renderer = renderer;
      this.deviceType = deviceType;
      this.currentState = 'LOADING';
      this.currentFrame = 0;

      this.introTimeline = null;
      this.introLoopTimeline = null;
      this.finalLoopTimeline = null;
      this.scrollTrigger = null;
      // FINAL_LOOP 专用素材加载器（config.finalLoopSource 存在时由 init 注入）
      this.finalLoopLoader = null;

      // 「刚解锁」窗口截止时间戳：在此之前屏蔽 INTRO_LOOP→SCRUB 触发，
      // 丢弃解锁瞬间 ScrollTrigger.refresh 带来的积压滚动误触发。
      this.justUnlockedUntil = 0;
      // 早锁是否已释放：保证延迟解锁 + 防积压窗口只在首次进入 INTRO_LOOP 生效，
      // 避免 SCRUB 上滑回 INTRO_LOOP 时重复进入 250ms 屏蔽窗口造成卡顿。
      this.scrollLockReleased = false;
      // 解锁兜底定时器：loop 半圈未触发解锁时强制放行
      this.unlockFallbackTimer = null;

      // 标题元素引用
      this.titleUniverseTime = document.querySelector('#title-universe-time');
      this.titlePlayWithTime = document.querySelector('#title-play-with-time');

      // Line Reveal 状态
      this.universeTimeRevealed = false;
      this.playWithTimeRevealed = false;
      this.splitTextInitialized = false;

      // 初始化
      this.initSplitText();
    }

    /**
     * 日志前缀
     */
    get logPrefix() {
      return `[YD ProductSequence ${this.deviceType}]`;
    }

    /**
     * 初始化 SplitText - 为标题准备 Line Reveal 动效
     */
    initSplitText() {
      if (this.splitTextInitialized) return;
      this.splitTextInitialized = true;

      // 分割 Universe Time
      if (this.titleUniverseTime) {
        this.splitUniverseTime = new SplitText(this.titleUniverseTime, {
          type: "lines",
          linesClass: "yd-line"
        });

        this.splitUniverseTime.lines.forEach(line => {
          const wrapper = document.createElement('div');
          wrapper.className = 'yd-line-inner';
          wrapper.innerHTML = line.innerHTML;
          line.innerHTML = '';
          line.appendChild(wrapper);
        });
      }

      // 分割 World's Only 720° 标题
      if (this.titlePlayWithTime) {
        this.splitPlayWithTime = new SplitText(this.titlePlayWithTime, {
          type: "lines",
          linesClass: "yd-line"
        });

        this.splitPlayWithTime.lines.forEach(line => {
          const wrapper = document.createElement('div');
          wrapper.className = 'yd-line-inner';
          wrapper.innerHTML = line.innerHTML;
          line.innerHTML = '';
          line.appendChild(wrapper);
        });

        // 把内嵌小视频按钮注入到最后一行末尾，跟随 line reveal 一起上浮
        this.injectInlineVideoTrigger();
      }
    }

    /**
     * 从模板里复制小视频按钮，挂在第二行标题尾部
     * 移动端（<768px）跳过：节省视频解码 / dialog 绑定开销
     */
    injectInlineVideoTrigger() {
      if (window.innerWidth < 768) return;

      const template = document.querySelector('#title-video-trigger-template');
      if (!template) return;

      const lines = this.titlePlayWithTime.querySelectorAll('.yd-line-inner');
      const lastLine = lines[lines.length - 1];
      if (!lastLine) return;

      const fragment = template.content.cloneNode(true);
      lastLine.appendChild(fragment);

      const trigger = lastLine.querySelector('.title-video-trigger');
      if (!trigger) return;

      this.bindVideoDialog(trigger);
    }

    /**
     * 绑定小视频按钮的点击 / 弹窗关闭逻辑
     */
    bindVideoDialog(trigger) {
      const dialog = document.querySelector('#title-video-dialog');
      const fullVideo = document.querySelector('#title-video-full');
      if (!dialog || !fullVideo) return;

      const closeBtn = dialog.querySelector('.title-video-dialog__close');

      const openDialog = () => {
        if (typeof dialog.showModal === 'function' && !dialog.open) {
          dialog.showModal();
          fullVideo.currentTime = 0;
          const playPromise = fullVideo.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
          }
        }
      };

      const closeDialog = () => {
        if (dialog.open) {
          fullVideo.pause();
          dialog.close();
        }
      };

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        openDialog();
      });

      if (closeBtn) {
        closeBtn.addEventListener('click', closeDialog);
      }

      // 点击背景空白处关闭（点击 dialog 自身但不是内容）
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) {
          closeDialog();
        }
      });

      // ESC 关闭（dialog 自带，但要同步暂停视频）
      dialog.addEventListener('close', () => {
        fullVideo.pause();
      });
    }

    /**
     * 解析滚动高度配置
     */
    parseScrollHeight(value) {
      // 优先用 locked-vh 锁定值，避开 iOS Safari 地址栏导致的 svh 漂移
      const lockedVH = window.YDLockedVH && window.YDLockedVH.value;
      const viewportHeight = lockedVH ? lockedVH * 100 : window.innerHeight;
      if (typeof value === 'number') return value;
      if (typeof value === 'string' && (value.endsWith('vh') || value.endsWith('svh'))) {
        return (parseFloat(value) / 100) * viewportHeight;
      }
      return viewportHeight * 2; // 默认 200svh
    }

    /**
     * 状态转换
     */
    transitionTo(newState) {
      this.cleanup();
      this.currentState = newState;

      switch (newState) {
        case 'INTRO':
          this.enterIntro();
          break;
        case 'INTRO_LOOP':
          this.enterIntroLoop();
          break;
        case 'SCRUB':
          this.enterScrub();
          break;
        case 'FINAL_LOOP':
          this.enterFinalLoop();
          break;
      }
    }

    /**
     * SCRUB 期间的标题动画 (基于 progress)
     */
    updateTitlesForScrub(progress) {
      const playWithTimeInners = this.titlePlayWithTime.querySelectorAll('.yd-line-inner');

      // Universe Time 淡出 (0-12.5%)
      if (progress < 0.125) {
        const fadeOut = progress / 0.125;
        gsap.set(this.titleUniverseTime, { opacity: 1 - fadeOut });
        gsap.set(this.titlePlayWithTime, { opacity: 0 });
      }
      // World's Only 720° 淡入 + Line Reveal (12.5-37.5%)
      else if (progress < 0.375) {
        const fadeIn = (progress - 0.125) / 0.25;
        gsap.set(this.titleUniverseTime, { opacity: 0 });
        gsap.set(this.titlePlayWithTime, { opacity: fadeIn });

        // Line Reveal 动画（只在首次触发）
        if (!this.playWithTimeRevealed && fadeIn > 0.1) {
          this.playWithTimeRevealed = true;

          gsap.fromTo(
            playWithTimeInners,
            { yPercent: 100 },
            { yPercent: 0, duration: 0.8, stagger: 0.12, ease: 'power3.out' }
          );
        }
      }
      // World's Only 720° 保持显示 (37.5-100%)，进入 FINAL_LOOP 后也不淡出
      else {
        gsap.set(this.titleUniverseTime, { opacity: 0 });
        gsap.set(this.titlePlayWithTime, { opacity: 1 });
      }
    }

    /**
     * 启动最终 LOOP 动画 (FINAL_LOOP 帧循环)
     */
    startFinalLoopAnimation() {
      const { finalLoopFrames, finalLoopFps, finalLoopSource } = this.config;
      // 素材本身是 30fps；调低 finalLoopFps 即整体放慢（如 15 = 慢一倍）
      const fps = finalLoopFps > 0 ? finalLoopFps : 30;

      // 配了 finalLoopSource 且已就绪时，只有 FINAL_LOOP 换用该素材，
      // 帧号改走它的内部索引（0 → finalLoopSource.start）；其余阶段仍用主素材
      const useAltSource = !!(this.finalLoopLoader && finalLoopSource);
      const frameCount = useAltSource
        ? finalLoopSource.end - finalLoopSource.start
        : finalLoopFrames.end - finalLoopFrames.start;
      const startFrame = useAltSource ? 0 : finalLoopFrames.start;
      const finalLoopDuration = frameCount / fps;

      if (this.finalLoopTimeline) {
        this.finalLoopTimeline.kill();
      }

      this.finalLoopTimeline = gsap.timeline({
        repeat: -1,
        yoyo: false,
        onUpdate: () => {
          if (this.currentState === 'FINAL_LOOP') {
            const progress = this.finalLoopTimeline.progress();
            const frame = Math.floor(startFrame + progress * frameCount);
            if (useAltSource) {
              this.renderFinalLoopFrame(frame);
            } else {
              this.renderFrame(frame);
            }
          }
        }
      });

      this.finalLoopTimeline.to({}, { duration: finalLoopDuration });
    }

    /**
     * 用 FINAL_LOOP 专用素材渲染（frameNumber 是该 loader 的内部索引）
     */
    renderFinalLoopFrame(frameNumber) {
      const img = this.finalLoopLoader.getFrameSafe(frameNumber);
      if (img) {
        this.renderer.renderFrame(img);
        return;
      }
      // 专用素材尚无任何帧就绪（它现在延后到 INTRO_LOOP 才开始加载，慢网下可能还没到）：
      // 临时用主素材 125–174 段等比映射，画面继续动而不是冻在最后一帧；有帧后自动切回
      const { finalLoopFrames, finalLoopSource } = this.config;
      if (!finalLoopFrames || !finalLoopSource) return;
      const altCount = Math.max(1, finalLoopSource.end - finalLoopSource.start);
      const mainCount = finalLoopFrames.end - finalLoopFrames.start;
      this.renderFrame(finalLoopFrames.start + Math.floor((frameNumber / altCount) * mainCount));
    }

    /**
     * 启动 FINAL_LOOP 专用素材的后台加载（幂等）。
     * 由 enterIntroLoop 调用：此时首屏关键帧已就绪、用户即将可滚动，而不是页面一打开就与
     * 首屏帧抢带宽（该素材 200 帧约 15.6MB，只有 SCRUB 走完才用到）。
     */
    kickFinalLoopPreload() {
      if (this._finalLoopKicked || typeof this._startFinalLoopPreload !== 'function') return;
      this._finalLoopKicked = true;
      this._startFinalLoopPreload();
    }

    stopFinalLoopAnimation() {
      if (this.finalLoopTimeline) {
        this.finalLoopTimeline.kill();
        this.finalLoopTimeline = null;
      }
    }

    /**
     * 停止 INTRO_LOOP 动画
     */
    stopIntroLoopAnimation() {
      if (this.introLoopTimeline) {
        this.introLoopTimeline.kill();
        this.introLoopTimeline = null;
      }
    }

    renderFrame(frameNumber) {
      this.currentFrame = frameNumber;
      const img = this.loader.getFrameSafe(frameNumber);
      if (img) {
        this.renderer.renderFrame(img);
      }
    }

    /**
     * 归零原子操作：把 Lenis / 原生 scroll / ScrollTrigger 三者拉齐到顶部。
     * 复用 yd-scroll-early-lock release 同款逻辑。幂等、可重复调用。
     * 运行时回顶用轻量 refresh()（不传 true，避免全量重算开销）。
     */
    snapToTop() {
      // refresh 会同步触发 pin 的 onUpdate，progress 可能瞬时跳变越过 SCRUB 阈值。
      // 先开 250ms 保护窗口，让 onUpdate 在窗口内丢弃这次跳变，避免误触发 INTRO_LOOP→SCRUB。
      this.justUnlockedUntil = Date.now() + 250;
      // 全量 refresh 在本页实测 350–450ms，而每次从 SCRUB 上滑回 INTRO_LOOP 都会走到这里。
      // 只有 pin 的实际滚动距离与按当前 locked-vh 算出的期望值不一致（布局变过）时才重算，
      // 正常回顶只做 scroll / Lenis 归零 + update，消除上滑到顶时的停顿。
      if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
        const st = this.scrollTrigger;
        const expected = this.parseScrollHeight(this.config.scrollHeight);
        const stale = !st || Math.abs((st.end - st.start) - expected) > 1;
        if (stale) window.ScrollTrigger.refresh();
      }
      try { window.scrollTo(0, 0); } catch (e) {}
      if (window.YDLenis && typeof window.YDLenis.scrollTo === 'function') {
        window.YDLenis.scrollTo(0, { immediate: true, force: true });
      }
      if (window.ScrollTrigger && typeof window.ScrollTrigger.update === 'function') {
        window.ScrollTrigger.update();
      }
    }

    /**
     * 第二层兜底：绑定 scroll 监听 + 防抖，滚动停止后探测「卡在无人区」并收口。
     * 幂等：只绑一次。timer id 存实例字段，dispose 时清理。
     */
    bindTopGuard() {
      if (this._onScroll) return;
      this._topGuardTimer = null;
      this._onScroll = () => {
        clearTimeout(this._topGuardTimer);
        this._topGuardTimer = setTimeout(() => this.onScrollSettle(), 150);
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });
    }

    /**
     * 滚动停止后判定：只在「停在 1px ~ pin距离 的无人区、且非 SCRUB 主动推进」时收口。
     * 三重守卫（防抖 + 无人区阈值 + 非 SCRUB）防止误伤正常滚动。
     */
    onScrollSettle() {
      const y = (window.YDLenis && typeof window.YDLenis.scroll === 'number')
        ? window.YDLenis.scroll
        : window.scrollY;
      if (y > 50 && y < (this.pinDistance || 0) && this.currentState !== 'SCRUB') {
        this.snapToTop();
      }
    }

    /**
     * 清理当前状态的资源
     */
    cleanup() {
      this.playWithTimeRevealed = false;
      this.universeTimeRevealed = false;

      if (this.introTimeline) {
        this.introTimeline.kill();
        this.introTimeline = null;
      }

      this.stopIntroLoopAnimation();
    }

    dispose() {
      this.cleanup();
      this.stopFinalLoopAnimation();

      if (this.unlockFallbackTimer) {
        clearTimeout(this.unlockFallbackTimer);
        this.unlockFallbackTimer = null;
      }

      if (this.scrollTrigger) {
        this.scrollTrigger.kill();
        this.scrollTrigger = null;
      }

      if (this._onScroll) {
        window.removeEventListener('scroll', this._onScroll);
        this._onScroll = null;
      }
      clearTimeout(this._topGuardTimer);
      this._topGuardTimer = null;
      this.pinDistance = 0;

      this.renderer.clear();
    }

    // 以下方法需要子类实现或覆盖
    enterIntro() {
      throw new Error('enterIntro must be implemented by subclass');
    }

    enterIntroLoop() {
      throw new Error('enterIntroLoop must be implemented by subclass');
    }

    enterScrub() {
      throw new Error('enterScrub must be implemented by subclass');
    }

    enterFinalLoop() {
      throw new Error('enterFinalLoop must be implemented by subclass');
    }
  }

  /**
   * 产品序列帧状态机 - 桌面端 Scrub 模式
   */
  class ProductSequenceStateMachineDesktop extends ProductSequenceStateMachineBase {
    constructor(config, loader, renderer) {
      super(config, loader, renderer, 'Desktop');
    }

    /**
     * INTRO 状态: 自动播放 0-50 帧，同时创建 ScrollTrigger (一开始就 pin)
     */
    enterIntro() {
      const { introFrames, introDuration, scrubFrames, scrollHeight, autoScrollSpeed, autoScrollIdleTimeout } = this.config;
      const scrollDistance = this.parseScrollHeight(scrollHeight);
      this.pinDistance = scrollDistance;   // 供第二层兜底探测阈值
      this.bindTopGuard();                 // 绑定回顶兜底（幂等）

      const wrapper = document.querySelector('#sequence-canvas-wrapper');

      // 记录上一次 progress，用于检测滚动方向
      let lastProgress = 0;

      // 创建 ScrollTrigger (pin)
      // 使用 pinSpacing: true 让 ScrollTrigger 自动管理间距，避免手动设置高度导致的时序问题
      this.scrollTrigger = ScrollTrigger.create({
        trigger: wrapper,
        start: 'top top',
        // 函数式 end：refresh（横竖屏 / 大幅视口变化改了 --locked-vh）时按新 vh 重算 pin 距离，
        // 静态字符串会让 pin 距离停留在创建时的值，与 parseScrollHeight 永久不一致
        end: () => {
          this.pinDistance = this.parseScrollHeight(scrollHeight);
          return '+=' + this.pinDistance;
        },
        pin: wrapper,
        pinSpacing: true,
        scrub: 1,
        refreshPriority: 10,  // 最高优先级，确保先于下游 section 刷新
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const progress = self.progress;
          const direction = progress > lastProgress ? 1 : -1;

          if (this.currentState === 'INTRO_LOOP') {
            // 刚解锁窗口内：丢弃积压滚动造成的 progress 跳变，不触发 SCRUB，
            // 并把 lastProgress 对齐当前值，避免下一帧误判方向。
            if (Date.now() < this.justUnlockedUntil) {
              lastProgress = progress;
              return;
            }
            if (progress > 0.01 && direction > 0) {
                            this.transitionTo('SCRUB');
            }
            lastProgress = progress;
            return;
          }

          if (this.currentState === 'INTRO') {
            lastProgress = progress;
            return;
          }

          if (this.currentState === 'SCRUB') {
            if (progress < 0.01 && direction < 0) {
                            this.transitionTo('INTRO_LOOP');
              lastProgress = progress;
              return;
            }

            const { introLoopFrames } = this.config;
            const scrubStartFrame = introLoopFrames.end;
            const scrubEndFrame = scrubFrames.end;
            const frame = Math.floor(
              scrubStartFrame + progress * (scrubEndFrame - scrubStartFrame)
            );
            this.renderFrame(frame);
            this.updateTitlesForScrub(progress);
          }

          lastProgress = progress;
        },
        onLeave: () => {
          if (this.currentState === 'SCRUB') {
                        this.transitionTo('FINAL_LOOP');
          }
        },
        onEnterBack: () => {
                    if (this.currentState === 'FINAL_LOOP') {
            this.handleEnterBackFromFinalLoop(autoScrollSpeed, autoScrollIdleTimeout);
          }
        },
        onLeaveBack: () => {
          if (this.currentState === 'SCRUB') {
            this.transitionTo('INTRO_LOOP');
          }
        }
      });

      // 播放 INTRO 动画 (0-50帧)
      this.introTimeline = gsap.timeline({
        onUpdate: () => {
          const progress = this.introTimeline.progress();
          const frame = Math.floor(
            introFrames.start + progress * (introFrames.end - introFrames.start)
          );
          this.renderFrame(frame);
        },
        onComplete: () => {
          this.renderFrame(introFrames.end);
          this.playUniverseTimeReveal();
          this.transitionTo('INTRO_LOOP');
          // 解锁延后到 INTRO_LOOP 跑满半圈后（见 enterIntroLoop），
          // 确保「确实进入 loop 阶段」再放行，避免 loading 期积压滚动一冲到底。
        }
      });

      this.introTimeline.to({}, { duration: introDuration });
    }

    /**
     * 从 FINAL_LOOP 回滚时的处理
     */
    handleEnterBackFromFinalLoop(autoScrollSpeed, autoScrollIdleTimeout) {
      this.stopFinalLoopAnimation();
      this.currentState = 'SCRUB';

      this.playWithTimeRevealed = false;
    }

    /**
     * 播放 Universe Time 标题 Line Reveal
     */
    playUniverseTimeReveal() {
      const universeTimeInners = this.titleUniverseTime.querySelectorAll('.yd-line-inner');

      gsap.to(this.titleUniverseTime, {
        opacity: 1,
        duration: 0.8,
        ease: 'power2.out',
        onStart: () => {
          gsap.set(this.titlePlayWithTime, { opacity: 0 });
        }
      });

      gsap.fromTo(
        universeTimeInners,
        { yPercent: 100 },
        { yPercent: 0, duration: 1.0, stagger: 0.12, ease: 'power3.out', delay: 0.1 }
      );
    }

    /**
     * INTRO_LOOP 状态
     */
    enterIntroLoop() {

      // 派发事件通知下游 section (video-scrub) 可以开始初始化
      window.dispatchEvent(new CustomEvent('yd-product-sequence-ready', {
        detail: { state: 'INTRO_LOOP', timestamp: Date.now(), device: 'desktop' }
      }));

      const { introLoopFrames, loopDuration } = this.config;

      this.playWithTimeRevealed = false;

      this.resetTitlesForIntroLoop();
      this.snapToTop();   // 第一层：正常回 loop 时确保 scroll/pin/Lenis 真正归零

      this.introLoopTimeline = gsap.timeline({
        repeat: -1,
        yoyo: false,
        onUpdate: () => {
          if (this.currentState === 'INTRO_LOOP') {
            const progress = this.introLoopTimeline.progress();

            // 首次进入 loop 跑过半圈 → 释放早锁（仅一次）
            if (!this.scrollLockReleased && progress >= 0.5) {
              this.releaseScrollLock();
            }

            const frame = Math.floor(
              introLoopFrames.start + progress * (introLoopFrames.end - introLoopFrames.start)
            );
            this.renderFrame(frame);
          }
        }
      });

      this.introLoopTimeline.to({}, { duration: loopDuration });

      // 兜底：正常情况下 loop 跑半圈（loopDuration*0.5）就会解锁；
      // 若 timeline 异常未触发 onUpdate，用 setTimeout 强制解锁，
      // 避免一直卡到 early-lock 的 60s 兜底。仅首次进入 loop 挂一次。
      if (!this.scrollLockReleased && !this.unlockFallbackTimer) {
        this.unlockFallbackTimer = setTimeout(() => {
          this.releaseScrollLock();
        }, loopDuration * 0.5 * 1000 + 400);
      }

      this.loader.continuePreload();
      this.kickFinalLoopPreload();
    }

    /**
     * 释放早锁并开启「刚解锁」窗口
     *
     * releaseScrollLock 内部会 ScrollTrigger.refresh + scrollTo(0,0) + Lenis immediate 重置，
     * 把积压滚动归零。但 refresh 后这一两帧 onUpdate 的 progress 跳变可能瞬间越过
     * SCRUB 触发阈值（progress > 0.01），所以同时开一个短窗口屏蔽这期间的 SCRUB 触发，
     * 丢弃积压滚动造成的误触发，根治「上滑回不到 loop」。
     */
    releaseScrollLock() {
      if (this.scrollLockReleased) return;
      this.scrollLockReleased = true;
      if (this.unlockFallbackTimer) {
        clearTimeout(this.unlockFallbackTimer);
        this.unlockFallbackTimer = null;
      }
      this.justUnlockedUntil = Date.now() + 250;
      window.__sequenceScrollRelease?.();
    }

    /**
     * 重置标题到 INTRO_LOOP 状态
     */
    resetTitlesForIntroLoop() {
      if (this.titleUniverseTime) {
        gsap.set(this.titleUniverseTime, { opacity: 1 });
      }
      if (this.titlePlayWithTime) {
        gsap.set(this.titlePlayWithTime, { opacity: 0 });
        const playWithTimeInners = this.titlePlayWithTime.querySelectorAll('.yd-line-inner');
        gsap.set(playWithTimeInners, { yPercent: 100 });
      }
    }

    /**
     * SCRUB 状态
     */
    enterScrub() {

      this.playWithTimeRevealed = false;
      this.universeTimeRevealed = false;
    }

    /**
     * FINAL_LOOP 状态
     *
     * 顶部第二个标题 (#title-play-with-time) 保持显示，不再切换到 final-sides
     */
    enterFinalLoop() {
      gsap.set(this.titleUniverseTime, { opacity: 0 });
      gsap.set(this.titlePlayWithTime, { opacity: 1 });

      this.startFinalLoopAnimation();
    }
  }

  /**
   * 产品序列帧状态机 - 移动端 Scrub 模式
   */
  class ProductSequenceStateMachineMobile extends ProductSequenceStateMachineBase {
    constructor(config, loader, renderer) {
      super(config, loader, renderer, 'Mobile');
    }

    /**
     * INTRO 状态
     */
    enterIntro() {
      const { introFrames, introDuration, scrubFrames, scrollHeight, autoScrollSpeed, autoScrollIdleTimeout } = this.config;
      const scrollDistance = this.parseScrollHeight(scrollHeight);
      this.pinDistance = scrollDistance;   // 供第二层兜底探测阈值
      this.bindTopGuard();                 // 绑定回顶兜底（幂等）

      const wrapper = document.querySelector('#sequence-canvas-wrapper');

      let lastProgress = 0;

      // 使用 pinSpacing: true 让 ScrollTrigger 自动管理间距，避免手动设置高度导致的时序问题
      this.scrollTrigger = ScrollTrigger.create({
        trigger: wrapper,
        start: 'top top',
        // 函数式 end：refresh（横竖屏 / 大幅视口变化改了 --locked-vh）时按新 vh 重算 pin 距离，
        // 静态字符串会让 pin 距离停留在创建时的值，与 parseScrollHeight 永久不一致
        end: () => {
          this.pinDistance = this.parseScrollHeight(scrollHeight);
          return '+=' + this.pinDistance;
        },
        pin: wrapper,
        pinSpacing: true,
        scrub: 1,
        refreshPriority: 10,  // 最高优先级，确保先于下游 section 刷新
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const progress = self.progress;
          const direction = progress > lastProgress ? 1 : -1;

          if (this.currentState === 'INTRO_LOOP') {
            // 刚解锁/刚收口窗口内：丢弃 refresh 带来的 progress 跳变，不触发 SCRUB
            if (Date.now() < this.justUnlockedUntil) {
              lastProgress = progress;
              return;
            }
            if (progress > 0.01 && direction > 0) {
                            this.transitionTo('SCRUB');
            }
            lastProgress = progress;
            return;
          }

          if (this.currentState === 'INTRO') {
            lastProgress = progress;
            return;
          }

          if (this.currentState === 'SCRUB') {
            if (progress < 0.01 && direction < 0) {
                            this.transitionTo('INTRO_LOOP');
              lastProgress = progress;
              return;
            }

            const { introLoopFrames } = this.config;
            const scrubStartFrame = introLoopFrames.end;
            const scrubEndFrame = scrubFrames.end;
            const frame = Math.floor(
              scrubStartFrame + progress * (scrubEndFrame - scrubStartFrame)
            );
            this.renderFrame(frame);
            this.updateTitlesForScrub(progress);
          }

          lastProgress = progress;
        },
        onLeave: () => {
          if (this.currentState === 'SCRUB') {
                        this.transitionTo('FINAL_LOOP');
          }
        },
        onEnterBack: () => {
                    if (this.currentState === 'FINAL_LOOP') {
            this.handleEnterBackFromFinalLoop(autoScrollSpeed, autoScrollIdleTimeout);
          }
        }
      });

      this.introTimeline = gsap.timeline({
        onUpdate: () => {
          const progress = this.introTimeline.progress();
          const frame = Math.floor(
            introFrames.start + progress * (introFrames.end - introFrames.start)
          );
          this.renderFrame(frame);
        },
        onComplete: () => {
          this.renderFrame(introFrames.end);
          this.playUniverseTimeReveal();
          this.transitionTo('INTRO_LOOP');
          // 释放 section 模板内同步内联 JS 注入的早锁（intro 帧序列播完才允许滚动）
          window.__sequenceScrollRelease?.();
        }
      });

      this.introTimeline.to({}, { duration: introDuration });
    }

    /**
     * 从 FINAL_LOOP 回滚时的处理
     */
    handleEnterBackFromFinalLoop(autoScrollSpeed, autoScrollIdleTimeout) {
      this.stopFinalLoopAnimation();
      this.currentState = 'SCRUB';

      this.playWithTimeRevealed = false;
    }

    /**
     * 播放 Universe Time 标题 Line Reveal
     */
    playUniverseTimeReveal() {
      const universeTimeInners = this.titleUniverseTime.querySelectorAll('.yd-line-inner');

      gsap.to(this.titleUniverseTime, {
        opacity: 1,
        duration: 0.8,
        ease: 'power2.out',
        onStart: () => {
          gsap.set(this.titlePlayWithTime, { opacity: 0 });
        }
      });

      gsap.fromTo(
        universeTimeInners,
        { yPercent: 100 },
        { yPercent: 0, duration: 1.0, stagger: 0.12, ease: 'power3.out', delay: 0.1 }
      );
    }

    /**
     * INTRO_LOOP 状态
     */
    enterIntroLoop() {

      // 派发事件通知下游 section (video-scrub) 可以开始初始化
      window.dispatchEvent(new CustomEvent('yd-product-sequence-ready', {
        detail: { state: 'INTRO_LOOP', timestamp: Date.now(), device: 'mobile' }
      }));

      const { introLoopFrames, loopDuration } = this.config;

      this.playWithTimeRevealed = false;

      this.resetTitlesForIntroLoop();
      this.snapToTop();   // 第一层：正常回 loop 时确保 scroll/pin/Lenis 真正归零

      this.introLoopTimeline = gsap.timeline({
        repeat: -1,
        yoyo: false,
        onUpdate: () => {
          if (this.currentState === 'INTRO_LOOP') {
            const progress = this.introLoopTimeline.progress();
            const frame = Math.floor(
              introLoopFrames.start + progress * (introLoopFrames.end - introLoopFrames.start)
            );
            this.renderFrame(frame);
          }
        }
      });

      this.introLoopTimeline.to({}, { duration: loopDuration });

      this.loader.continuePreload();
      this.kickFinalLoopPreload();
    }

    /**
     * 重置标题到 INTRO_LOOP 状态
     */
    resetTitlesForIntroLoop() {
      if (this.titleUniverseTime) {
        gsap.set(this.titleUniverseTime, { opacity: 1 });
      }
      if (this.titlePlayWithTime) {
        gsap.set(this.titlePlayWithTime, { opacity: 0 });
        const playWithTimeInners = this.titlePlayWithTime.querySelectorAll('.yd-line-inner');
        gsap.set(playWithTimeInners, { yPercent: 100 });
      }
    }

    /**
     * SCRUB 状态
     */
    enterScrub() {

      this.playWithTimeRevealed = false;
      this.universeTimeRevealed = false;
    }

    /**
     * FINAL_LOOP 状态
     *
     * 顶部第二个标题 (#title-play-with-time) 保持显示，不再切换到 final-sides
     */
    enterFinalLoop() {
      gsap.set(this.titleUniverseTime, { opacity: 0 });
      gsap.set(this.titlePlayWithTime, { opacity: 1 });

      this.startFinalLoopAnimation();
    }
  }

  /**
   * 初始化函数
   */
  async function initProductSequence() {
    // 防止 HotReload 导致的重复初始化
    if (window.ydProductSequenceStateMachine) {
            return;
    }

    // 移动端走独立模块 yd-product-sequence-mobile.js，桌面端模块直接退出
    if (window.innerWidth < 768) {
      return;
    }

    const section = document.querySelector('#product-sequence-section');
    if (!section) {
      return;
    }

    const configData = section.dataset.config;
    if (!configData) {
            return;
    }

    const config = JSON.parse(configData);

    // UI 元素
    const loadingScreen = document.getElementById('loading-screen');
    const loadingProgress = document.getElementById('loading-progress');
    const loadingPercentage = document.getElementById('loading-percentage');

    // 检测设备类型（移动端已在入口早返回，这里只走桌面/平板路径）
    const isDesktop = window.YDDevice ? window.YDDevice.isDesktop() : window.innerWidth >= 1024;
    const basePath = config.framePath;

    
    try {
      // 1. 创建加载器
      const loader = new LocalSequenceLoader({
        totalFrames: config.totalFrames,
        basePath: basePath,
        avifPath: config.avifPath,
        fallbackPath: config.fallbackPath,
        priorityBase: 0   // 首屏序列，全站最高优先级
      });

      // 2. 预加载优先帧
      await loader.preloadPriority(
        config.priorityFrames,
        (progress) => {
          const percent = Math.round(progress * 100);
          if (loadingProgress) {
            loadingProgress.style.width = percent + '%';
          }
          if (loadingPercentage) {
            loadingPercentage.textContent = percent + '%';
          }

          if (progress >= config.hideLoadingAt && loadingScreen) {
            loadingScreen.classList.add('fade-out');
          }
        }
      );

      // 3. 创建渲染器
      const renderer = new CanvasRenderer('#product-sequence-canvas');

      // 4. 根据设备类型选择状态机
      const stateMachine = isDesktop
        ? new ProductSequenceStateMachineDesktop(config, loader, renderer)
        : new ProductSequenceStateMachineMobile(config, loader, renderer);
      // 非优先帧按离当前播放帧的距离加载（SCRUB 起点附近先到）
      loader.getFocusFrame = () => stateMachine.currentFrame;

      // 4.5 FINAL_LOOP 专用素材（可选）：独立一套 loader，后台加载不进 loading 进度条。
      // 加载时机延后到 INTRO_LOOP（见 kickFinalLoopPreload），并排在主素材剩余帧之后，
      // 不再在页面一打开就与首屏关键帧抢带宽。
      // 未加载完就进入 FINAL_LOOP 时 getFrameSafe 回退最近已加载帧；一帧都没有时
      // renderFinalLoopFrame 临时映射到主素材；整体失败则置空，FINAL_LOOP 退回主素材。
      if (config.finalLoopSource) {
        const src = config.finalLoopSource;
        const finalLoopLoader = new LocalSequenceLoader({
          totalFrames: src.end - src.start + 1,
          basePath: src.basePath,
          avifPath: src.avifPath,
          fallbackPath: src.fallbackPath,
          framePad: src.framePad,
          frameStart: src.start,
          priorityBase: 20000   // 排在主素材（0）之后、下游 section（30000+）之前
        });
        stateMachine.finalLoopLoader = finalLoopLoader;

        stateMachine._startFinalLoopPreload = async () => {
          const supportsAvif = window.imageFormatDetector
            ? await window.imageFormatDetector.isSupported('avif')
            : false;
          finalLoopLoader.actualFormat = supportsAvif ? 'avif' : 'fallback';
          // continuePreload 逐帧写入且吞掉单帧失败，适合后台加载
          await finalLoopLoader.continuePreload(null);
          if (finalLoopLoader.loadedCount === 0) {
            console.warn(
              '[YD ProductSequence] finalLoopSource 一帧都没加载到，FINAL_LOOP 退回主素材:',
              src.basePath
            );
            stateMachine.finalLoopLoader = null;
          }
        };
      }

      // 5. 启动 INTRO 状态
      stateMachine.transitionTo('INTRO');

      // 保存到全局以便调试
      window.ydProductSequenceStateMachine = stateMachine;

    } catch (error) {
      
      if (loadingScreen) {
        loadingScreen.classList.add('fade-out');
      }
    }
  }

  /**
   * 等待 GSAP 和 ScrollTrigger 加载
   */
  function waitForDependencies() {
    return new Promise((resolve, reject) => {
      if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        resolve();
        return;
      }

      // 优先用 GSAPUtils.waitFor（一次性触发，无轮询开销）
      if (window.GSAPUtils && typeof window.GSAPUtils.waitFor === 'function') {
        const timer = setTimeout(() => reject(new Error('GSAP/ScrollTrigger not loaded')), 10000);
        window.GSAPUtils.waitFor(['ScrollTrigger'], () => {
          clearTimeout(timer);
          resolve();
        });
        return;
      }

      // 回退：轻量轮询（200ms）
      const check = setInterval(() => {
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
          clearInterval(check);
          resolve();
        }
      }, 200);

      setTimeout(() => {
        clearInterval(check);
        reject(new Error('GSAP/ScrollTrigger not loaded'));
      }, 10000);
    });
  }

  /**
   * 入口
   */
  async function init() {
    await waitForDependencies();
    await initProductSequence();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
